"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ChevronRight, Clock, Users, MapPin, Diamond } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
// import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { TrainingSkeleton } from "./_components/training-skeleton";
import ContactDialog from "./_components/contact-dialog";
import { FAQSection } from "@/components/layout/sections/faq";

interface TrainingData {
  _id: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  duration: string;
  schedule: {
    timeSlots: [
      {
        start: string;
        end: string;
      }
    ];
  };
  badges: string[];
  audience: { title: string; description: string }[];
  program: { day: string; title: string; details: string[] }[];
  trainer: {
    name: string;
    role: string;
    experience: string[];
    avatar: string;
  };
  faq: { question: string; answer: string }[];
  modalites: {
    participants: string;
    duration: string;
    level: string;
    location: string;
    tool: string;
  };
}

const fetchTrainingData = async (
  slug: string
): Promise<{ training: TrainingData }> => {
  const response = await fetch(`/api/training/${slug}`);
  if (!response.ok) {
    throw new Error("Failed to fetch training data");
  }
  return response.json();
};

export default function TrainingPage({ params }: { params: { slug: string } }) {
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [trainingData, setTrainingData] = useState<TrainingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const mainHeader = document.querySelector("#training-header");
      if (mainHeader) {
        const headerRect = mainHeader.getBoundingClientRect();
        setIsHeaderVisible(headerRect.bottom > 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { training } = await fetchTrainingData(params.slug);
        setTrainingData(training);
      } catch (error) {
        console.error("Error fetching training data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen flex flex-col text-left">
      {/* Sticky Header - Shows when main header is not visible */}
      <AnimatePresence>
        {!isHeaderVisible && trainingData && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 left-0 right-0 bg-background border-b z-50 hidden lg:block"
          >
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between h-24">
                <div className="flex flex-col justify-center gap-1">
                  <h2 className="font-semibold">{trainingData.title}</h2>
                  <p className="text-md text-primary">
                    {trainingData.subtitle}
                  </p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-4 text-sm">
                    <Badge
                      variant="outline"
                      className="bg-black text-white border-none dark:border-solid dark:border-border"
                    >
                      {trainingData.duration}
                    </Badge>
                    <span className="text-muted-foreground">
                      {trainingData.schedule.timeSlots
                        .map((slot) => `${slot.start} - ${slot.end}`)
                        .join(" | ")}
                    </span>
                  </div>
                  <ContactDialog trainingId={trainingData._id}>
                    <Button className="bg-primary hover:bg-primary/90">
                      Nous contacter
                    </Button>
                  </ContactDialog>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="flex-1 container mx-auto px-4 py-8 pb-24 mt-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground overflow-x-auto pb-2">
          <Link
            href="/academy"
            className="hover:text-primary whitespace-nowrap"
          >
            L&apos;Academy
          </Link>
          <ChevronRight className="h-4 w-4 flex-shrink-0" />
          <span className="whitespace-nowrap">
            {isLoading ? "Chargement..." : trainingData?.title}
          </span>
        </nav>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-8">
            {isLoading ? (
              <TrainingSkeleton />
            ) : trainingData ? (
              <>
                {/* Header Card */}
                <Card
                  className="bg-background border border-secondary dark:border-border shadow-inner"
                  id="training-header"
                >
                  <CardContent className="pt-8">
                    <div className="space-y-6">
                      <div className="flex flex-col gap-4">
                        <div className="space-y-2">
                          <h1 className="text-3xl md:text-4xl font-bold">
                            {trainingData.title}
                          </h1>
                          <p className="text-lg md:text-xl text-primary">
                            {trainingData.subtitle}
                          </p>
                        </div>
                        <p className="text-sm md:text-base text-muted-foreground">
                          {trainingData.description}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {trainingData.badges.map((badge, index) => (
                          <Badge
                            key={index}
                            className={`
                              ${
                                index === 0
                                  ? "bg-[#E96A45] hover:bg-[#E96A45]/90 text-white"
                                  : ""
                              }
                              ${
                                index === 1
                                  ? "bg-[#FFDC26] hover:bg-[#FFDC26]/90 text-black"
                                  : ""
                              }
                              ${
                                index === 2
                                  ? "bg-[#ADAEFF] hover:bg-[#ADAEFF]/90 text-black"
                                  : ""
                              }
                              ${
                                index === 3
                                  ? "bg-[#699BF7] hover:bg-[#699BF7]/90 text-white"
                                  : ""
                              }
                              ${
                                index === 4
                                  ? "bg-[#0FA958] hover:bg-[#0FA958]/90 text-white"
                                  : ""
                              }
                              ${
                                index === 5
                                  ? "bg-[#217981] hover:bg-[#217981]/90 text-white"
                                  : ""
                              }
                            `}
                          >
                            {badge}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-sm md:text-base text-muted-foreground">
                        {trainingData.duration} de formation intensive qui
                        combine théorie et pratique
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Pour Qui */}
                <Card>
                  <CardHeader>
                    <div className="space-y-1.5">
                      <h2 className="text-xl md:text-2xl font-semibold">
                        Pour qui
                      </h2>
                      <p className="text-base md:text-lg text-primary">
                        La formation est conçue pour:
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {trainingData.audience.map((item, index) => (
                        <Card
                          key={index}
                          className="bg-[#F0ECFD] border-none dark:bg-[#2D2D2D]"
                        >
                          <CardContent className="pt-6">
                            <h3 className="font-semibold mb-2">{item.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {item.description}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Programme */}
                <Card>
                  <CardHeader>
                    <div className="space-y-1.5">
                      <h2 className="text-xl md:text-2xl font-semibold">
                        Le programme
                      </h2>
                      <p className="text-base md:text-lg text-primary">
                        Comment se déroule la formation?
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Accordion
                      type="multiple"
                      defaultValue={["jour-1", "jour-2", "jour-3"]}
                      className="w-full"
                    >
                      {trainingData.program.map((item, index) => (
                        <AccordionItem
                          key={`jour-${index + 1}`}
                          value={`jour-${index + 1}`}
                          className="border-none"
                        >
                          <AccordionTrigger className="hover:no-underline p-0">
                            <div className="flex items-start sm:items-center justify-between w-full py-4">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-left">
                                <Badge
                                  variant="outline"
                                  className="bg-black text-white border-none dark:border-solid dark:border-border whitespace-nowrap w-min"
                                >
                                  {item.day}
                                </Badge>
                                <span className="font-semibold">
                                  {item.title}
                                </span>
                              </div>
                              <span className="text-sm text-primary px-4 hidden sm:inline">
                                DÉTAIL
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="space-y-4 pl-4 md:pl-[76px] text-left">
                            <div className="space-y-2">
                              {/* <h4 className="font-medium">
                                Fondamentaux et principes avancés de design UI
                              </h4> */}
                              <ul className="space-y-1">
                                {item.details.map((detail, detailIndex) => (
                                  <li
                                    key={detailIndex}
                                    className="flex items-start gap-2 text-muted-foreground"
                                  >
                                    <Diamond className="h-3 w-3 text-primary flex-shrink-0 mt-1" />
                                    <span className="text-sm">{detail}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>

                {/* TODO */}
                {/* Formateur */}
                {/* <Card>
                  <CardHeader>
                    <div className="space-y-1.5">
                      <h2 className="text-xl md:text-2xl font-semibold">
                        Formateur(s)
                      </h2>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                      <Avatar className="h-20 w-20 dark:border">
                        <AvatarImage
                          src={trainingData.trainer.avatar}
                          alt={trainingData.trainer.name}
                        />
                        <AvatarFallback>
                          {trainingData.trainer.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="space-y-2 text-center sm:text-left">
                        <div>
                          <h3 className="font-semibold">
                            {trainingData.trainer.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {trainingData.trainer.role}
                          </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-sm text-muted-foreground">
                          {trainingData.trainer.experience.map((exp, index) => (
                            <span key={index}>
                              {exp}
                              {index <
                                trainingData.trainer.experience.length - 1 && (
                                <span className="hidden sm:inline">•</span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card> */}

                {/* FAQ */}
                <FAQSection faqItems={trainingData.faq} />
              </>
            ) : (
              <div>Une erreur s&apos;est produite.</div>
            )}
          </div>

          {/* Sidebar Column - Only visible on large screens */}
          <div className="hidden lg:block space-y-6">
            {isLoading ? (
              <Card className="border-none bg-[#F5F4FE] dark:bg-[#1C1B1F]">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <Skeleton className="h-8 w-1/2" />
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </CardContent>
              </Card>
            ) : trainingData ? (
              <Card className="border border-secondary dark:border-border shadow-inner bg-[#F5F4FE] dark:bg-[#1C1B1F]">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">Modalités</h2>

                        <div className="text-sm text-muted-foreground">
                          <p>
                            {trainingData.schedule.timeSlots
                              .map((slot) => `${slot.start} - ${slot.end}`)
                              .join(" | ")}
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator className="bg-[#E5E5E5] dark:bg-[#2D2D2D]" />

                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">
                            Participants
                          </span>
                          <span>{trainingData.modalites.participants}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Clock className="h-4 w-4 text-muted-foreground" />

                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">Durée</span>
                          <Badge
                            variant="outline"
                            className="bg-black text-white border-none"
                          >
                            {trainingData.modalites.duration}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">Niveau</span>
                          <span>{trainingData.modalites.level}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">
                            Présence
                          </span>
                          <span>{trainingData.modalites.location}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Diamond className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">
                            Outil nécessaire
                          </span>
                          <span>{trainingData.modalites.tool}</span>
                        </div>
                      </div>
                    </div>

                    <ContactDialog trainingId={trainingData._id}>
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        size="lg"
                      >
                        Nous contacter
                      </Button>
                    </ContactDialog>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            <Card className="border-none shadow-inner bg-[#E8F5E9] dark:bg-[#26302a]">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="rounded-full bg-[#4CAF50]/10 p-2">
                      <Diamond className="h-4 w-4 text-[#4CAF50]" />
                    </div>
                    <h3 className="font-semibold">Garantie de Satisfaction</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Ce cours est couvert par une garantie.
                  </p>
                  <Link
                    href="/politique-de-remboursement"
                    className="text-sm text-[#4CAF50] hover:underline"
                  >
                    Politique de remboursement
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Sheet */}
      <div className="fixed bottom-4 left-4 right-4 lg:hidden z-50">
        <Sheet>
          <SheetTrigger asChild>
            <Button className="w-full bg-primary hover:bg-primary/90" size="lg">
              Voir les détails du cours
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <SheetHeader>
              <SheetTitle>Détails du cours</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-8 w-1/2" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-40 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : trainingData ? (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge
                        variant="outline"
                        className="bg-black text-white border-none"
                      >
                        {trainingData.duration}
                      </Badge>
                      <div className="text-sm text-muted-foreground text-right">
                        <p>
                          {trainingData.schedule.timeSlots
                            .map((slot) => `${slot.start} - ${slot.end}`)
                            .join(" | ")}
                        </p>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">
                            Participants
                          </span>
                          <span>{trainingData.modalites.participants}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">Durée</span>
                          <span>{trainingData.modalites.duration}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">
                            Présence
                          </span>
                          <span>{trainingData.modalites.location}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">Niveau</span>
                          <span>{trainingData.modalites.level}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Diamond className="h-4 w-4 text-muted-foreground" />
                        <div className="flex justify-between flex-1">
                          <span className="text-muted-foreground">
                            Outil nécessaire
                          </span>
                          <span>{trainingData.modalites.tool}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Card className="border-none bg-[#E8F5E9] dark:bg-[#1C1F1D]">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <div className="rounded-full bg-[#4CAF50]/10 p-2">
                            <Diamond className="h-4 w-4 text-[#4CAF50]" />
                          </div>
                          <h3 className="font-semibold">
                            Garantie de Satisfaction
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Ce cours est couvert par une garantie.
                        </p>
                        <Link
                          href="/politique-de-remboursement"
                          className="text-sm text-[#4CAF50] hover:underline"
                        >
                          Politique de remboursement
                        </Link>
                      </div>
                    </CardContent>
                  </Card>

                  <ContactDialog trainingId={trainingData._id}>
                    <Button
                      className="w-full bg-primary hover:bg-primary/90"
                      size="lg"
                    >
                      Nous contacter
                    </Button>
                  </ContactDialog>
                </>
              ) : (
                <div>Failed to load training data</div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
